import React, { FC, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { toast } from "react-toastify";
import { toastOptions } from "config/constants";
import { useAppDispatch, useAppSelector } from "config/hooks";
import API, { MyInvitation } from "api/accounts";
import { completeSignIn } from "reducers/auth";
import { invitationSummary } from "Pages/Invitation";
import logger from "utils/logger";
import {
  closeInvitations,
  invitationRef,
  openInvitations,
  refreshInvitations,
  removeInvitation,
  resetInvitations,
  usePendingInvitations,
} from "./store";

const POLL_MS = 60_000;
const MIN_REFRESH_GAP_MS = 15_000;
// The dialog opens by itself once per tab session; afterwards the header
// "Invitations" button reopens it.
const AUTO_OPENED_KEY = "invitationsPromptAutoOpened";

const autoOpened = (email: string): boolean => {
  try {
    return sessionStorage.getItem(AUTO_OPENED_KEY) === email;
  } catch {
    return false;
  }
};

const markAutoOpened = (email: string): void => {
  try {
    sessionStorage.setItem(AUTO_OPENED_KEY, email);
  } catch (err) {
    logger.error(err);
  }
};

interface PendingInvitationsPromptProps {
  userEmail: string;
}

// Keeps the signed-in user's pending invitations fresh (on navigation and on
// an interval, so invitations sent while signed in show up) and renders the
// dialog to accept them.
const PendingInvitationsPrompt: FC<PendingInvitationsPromptProps> = ({
  userEmail,
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const { invitations, open } = usePendingInvitations();
  const [accepting, setAccepting] = useState<string | null>(null);
  const lastRefresh = useRef(0);

  const refresh = async (force = false) => {
    if (!force && Date.now() - lastRefresh.current < MIN_REFRESH_GAP_MS) return;
    lastRefresh.current = Date.now();
    const list = await refreshInvitations();
    if (list.length > 0 && !autoOpened(userEmail)) {
      markAutoOpened(userEmail);
      openInvitations();
    }
  };

  useEffect(() => {
    resetInvitations();
    refresh(true);
    const timer = setInterval(() => refresh(true), POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const accept = async (invitation: MyInvitation) => {
    setAccepting(invitationRef(invitation));
    try {
      const res =
        invitation.id != null
          ? await API.acceptMyInvitation(invitation.id)
          : await API.acceptInvitation(invitation.key as string);
      toast.success(
        `Invitation from ${invitation.account_name} accepted`,
        toastOptions,
      );
      removeInvitation(invitationRef(invitation));
      // Refresh the account list; switch to the account for a role invitation.
      const accountId = res.role === "payee" ? undefined : res.account_id;
      await completeSignIn(currentUser, accountId)(dispatch);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Could not accept the invitation",
        toastOptions,
      );
      refresh(true);
    } finally {
      setAccepting(null);
    }
  };

  return (
    <Dialog
      open={open && invitations.length > 0}
      onClose={closeInvitations}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {invitations.length === 1
          ? "You have an invitation"
          : "You have invitations"}
      </DialogTitle>
      <DialogContent>
        <List disablePadding>
          {invitations.map((invitation) => (
            <ListItem
              key={invitationRef(invitation)}
              divider
              secondaryAction={
                <Button
                  variant="contained"
                  disabled={accepting !== null}
                  onClick={() => accept(invitation)}
                >
                  Accept
                </Button>
              }
            >
              <ListItemText
                primary={invitationSummary({
                  status: "accept",
                  role: invitation.role,
                  account_name: invitation.account_name,
                })}
                secondary={`Sent ${new Date(invitation.created).toLocaleDateString()}`}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          onClick={closeInvitations}
          disabled={accepting !== null}
        >
          Later
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PendingInvitationsPrompt;
